import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { MailX, Loader2, CheckCircle2, Undo2, AlertTriangle, Sparkles } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { ThemeToggle } from '../components/landing/ThemeToggle';
import { usePublicTheme } from '../hooks/usePublicTheme';

export default function Unsubscribe() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') || '';
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [working, setWorking] = useState(false);
  const [done, setDone] = useState(false);      // unsubscribed in this session
  const [resubscribed, setResubscribed] = useState(false);
  const [defaultTheme, setDefaultTheme] = useState(null);
  const { theme: mode, toggle } = usePublicTheme(defaultTheme);

  useEffect(() => {
    const run = () => {
      if (!token) { setInvalid(true); setLoading(false); return; }
      api.get('/public/unsubscribe-info', { params: { t: token } })
        .then((r) => setInfo(r.data))
        .catch(() => setInvalid(true))
        .finally(() => setLoading(false));
      api.get('/public/landing').then((r) => setDefaultTheme(r.data?.default_theme || 'light')).catch(() => setDefaultTheme('light'));
    };
    const tm = setTimeout(run, 0);
    return () => clearTimeout(tm);
  }, [token]);

  const doUnsubscribe = async () => {
    setWorking(true);
    try {
      await api.post('/public/unsubscribe', { token });
      setDone(true); setResubscribed(false);
    } catch (e) { toast.error('Something went wrong. Please try again.'); }
    finally { setWorking(false); }
  };

  const doResubscribe = async () => {
    setWorking(true);
    try {
      await api.post('/public/resubscribe', { token });
      setResubscribed(true); setDone(false);
    } catch (e) { toast.error('Something went wrong. Please try again.'); }
    finally { setWorking(false); }
  };

  const alreadyOff = info?.unsubscribed || done;

  return (
    <div className="tg-public min-h-screen flex flex-col bg-background text-foreground" data-theme={mode} data-testid="unsubscribe-page">
      <div className="fixed top-4 right-4 z-50"><ThemeToggle theme={mode} onToggle={toggle} /></div>
      <header className="p-6 flex items-center gap-2.5">
        <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center"><Sparkles className="h-5 w-5 text-primary" /></div>
        <span className="font-display text-xl font-semibold">Thrive Gatherings</span>
      </header>

      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md text-center">
          {loading ? (
            <Loader2 className="h-7 w-7 animate-spin text-muted-foreground mx-auto" data-testid="unsubscribe-loading" />
          ) : invalid ? (
            <div data-testid="unsubscribe-invalid">
              <div className="h-14 w-14 rounded-2xl bg-secondary flex items-center justify-center mx-auto mb-4"><AlertTriangle className="h-7 w-7 text-muted-foreground" /></div>
              <h1 className="font-display text-3xl font-semibold">Link not valid</h1>
              <p className="text-muted-foreground mt-2">This unsubscribe link is invalid or has expired. If you keep getting unwanted emails, please reply to one of them to let the host know.</p>
            </div>
          ) : resubscribed ? (
            <div data-testid="unsubscribe-resubscribed">
              <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="h-7 w-7 text-primary" /></div>
              <h1 className="font-display text-3xl font-semibold">You&apos;re resubscribed</h1>
              <p className="text-muted-foreground mt-2"><span className="font-medium text-foreground">{info?.email}</span> will receive invitations from {info?.host} again.</p>
              <Button variant="outline" onClick={doUnsubscribe} disabled={working} className="mt-6 gap-2" data-testid="unsubscribe-again-button">
                {working ? <Loader2 className="h-4 w-4 animate-spin" /> : <MailX className="h-4 w-4" />} Unsubscribe instead
              </Button>
            </div>
          ) : alreadyOff ? (
            <div data-testid="unsubscribe-success">
              <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="h-7 w-7 text-primary" /></div>
              <h1 className="font-display text-3xl font-semibold">You&apos;re unsubscribed</h1>
              <p className="text-muted-foreground mt-2"><span className="font-medium text-foreground">{info?.email}</span> won&apos;t receive any more event emails from {info?.host}. Your details are kept private and won&apos;t be emailed again.</p>
              <p className="text-sm text-muted-foreground mt-4">Changed your mind?</p>
              <Button variant="outline" onClick={doResubscribe} disabled={working} className="mt-2 gap-2" data-testid="resubscribe-button">
                {working ? <Loader2 className="h-4 w-4 animate-spin" /> : <Undo2 className="h-4 w-4" />} Resubscribe
              </Button>
            </div>
          ) : (
            <div data-testid="unsubscribe-confirm">
              <div className="h-14 w-14 rounded-2xl bg-secondary flex items-center justify-center mx-auto mb-4"><MailX className="h-7 w-7 text-foreground" /></div>
              <h1 className="font-display text-3xl font-semibold">Unsubscribe?</h1>
              <p className="text-muted-foreground mt-2">You&apos;re about to stop receiving event emails from <span className="font-medium text-foreground">{info?.host}</span> at <span className="font-medium text-foreground">{info?.email}</span>.</p>
              <Button onClick={doUnsubscribe} disabled={working} className="mt-6 w-full h-11 gap-2" data-testid="confirm-unsubscribe-button">
                {working ? <Loader2 className="h-4 w-4 animate-spin" /> : <MailX className="h-4 w-4" />} Unsubscribe me
              </Button>
              <p className="text-xs text-muted-foreground mt-3">You can resubscribe anytime from this page.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
